import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";

interface TournamentLeaderboardProps {
  tournamentId: string;
  /** Compact mode for TV display */
  compact?: boolean;
}

export default function TournamentLeaderboard({ tournamentId, compact = false }: TournamentLeaderboardProps) {
  const qc = useQueryClient();

  const { data: players = [], isLoading } = useQuery({
    queryKey: ["tournament-leaderboard", tournamentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_players")
        .select("*")
        .eq("tournament_id", tournamentId)
        .order("total_points", { ascending: false });
      if (error) throw error;

      const userIds = (data ?? []).map((p: Record<string, unknown>) => p.user_id);
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level")
        .in("id", userIds);

      const profileMap = new Map((profiles ?? []).map((p: Record<string, unknown>) => [p.id, p]));

      // Count matches played per player from tournament_match_results
      const { data: rounds } = await supabase
        .from("tournament_rounds")
        .select("id, tournament_match_results(team1_players, team2_players, score_team1, score_team2, status)")
        .eq("tournament_id", tournamentId);

      const matchStats = new Map<string, { played: number; wins: number; losses: number }>();
      for (const round of rounds ?? []) {
        for (const match of (round as Record<string, unknown>).tournament_match_results ?? []) {
          if (match.status !== "completed") continue;
          const allPlayers = [...(match.team1_players ?? []), ...(match.team2_players ?? [])];
          const t1Won = (match.score_team1 ?? 0) > (match.score_team2 ?? 0);
          const winners = t1Won ? (match.team1_players ?? []) : (match.team2_players ?? []);
          const losers = t1Won ? (match.team2_players ?? []) : (match.team1_players ?? []);

          for (const pid of allPlayers) {
            if (!matchStats.has(pid)) matchStats.set(pid, { played: 0, wins: 0, losses: 0 });
            matchStats.get(pid)!.played++;
          }
          for (const pid of winners) {
            if (matchStats.has(pid)) matchStats.get(pid)!.wins++;
          }
          for (const pid of losers) {
            if (matchStats.has(pid)) matchStats.get(pid)!.losses++;
          }
        }
      }

      return (data ?? []).map((p: Record<string, unknown>) => ({
        ...p,
        profile: profileMap.get(p.user_id) ?? { full_name: "Okänd", level: 1 },
        stats: matchStats.get(p.user_id) ?? { played: 0, wins: 0, losses: 0 },
      }));
    },
    enabled: !!tournamentId,
  });

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel(`leaderboard-${tournamentId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "tournament_players", filter: `tournament_id=eq.${tournamentId}` }, () => {
        qc.invalidateQueries({ queryKey: ["tournament-leaderboard", tournamentId] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "tournament_match_results" }, () => {
        qc.invalidateQueries({ queryKey: ["tournament-leaderboard", tournamentId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tournamentId, qc]);

  if (isLoading) {
    return <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>;
  }

  if (players.length === 0) {
    return <p className="text-center text-muted-foreground py-6">Inga spelare ännu</p>;
  }

  if (compact) {
    return (
      <div className="space-y-1">
        {players.map((p: any, i: number) => (
          <div key={p.id} className="flex items-center justify-between px-3 py-1.5 rounded bg-gray-800/50">
            <div className="flex items-center gap-2">
              <span className="w-5 text-center font-bold text-sm text-gray-400">{i + 1}</span>
              <span className="font-medium text-sm">{p.profile?.full_name ?? "Okänd"}</span>
            </div>
            <span className="font-bold tabular-nums text-sm">{p.total_points}p</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="grid grid-cols-[2rem_1fr_auto_3rem_2.5rem_2.5rem] gap-2 px-3 py-1.5 text-xs text-muted-foreground font-medium">
        <span>#</span>
        <span>Spelare</span>
        <span />
        <span className="text-right">Poäng</span>
        <span className="text-center">V</span>
        <span className="text-center">F</span>
      </div>

      {players.map((p: any, i: number) => (
        <Card key={p.id} className={i < 3 ? "border-primary/20" : ""}>
          <CardContent className="p-3 grid grid-cols-[2rem_1fr_auto_3rem_2.5rem_2.5rem] gap-2 items-center">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted font-bold text-xs text-muted-foreground">
              {i < 3 ? (
                <Trophy className={`h-3.5 w-3.5 ${i === 0 ? "text-yellow-500" : i === 1 ? "text-gray-400" : "text-amber-700"}`} />
              ) : (
                i + 1
              )}
            </div>
            <p className="font-medium text-sm truncate">{p.profile?.full_name ?? "Okänd"}</p>
            <LevelBadge level={p.profile?.level ?? 1} size="sm" />
            <span className="text-sm font-bold tabular-nums text-right">{p.total_points}</span>
            <span className="text-xs text-center text-green-600 font-medium">{p.stats.wins}</span>
            <span className="text-xs text-center text-red-500 font-medium">{p.stats.losses}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
